"use client"

import { React, useCallback, useEffect, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { RefreshCw, Wallet } from "lucide-react"
import { Button } from "../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"

export const formatBtcAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") {
    return "0.00000000 BTC"
  }

  const numericAmount = Number(amount)
  if (!Number.isFinite(numericAmount)) {
    return `${amount} BTC`
  }

  return `${numericAmount.toFixed(8)} BTC`
}

export const shortenBtcIdentifier = (value) => {
  if (!value) {
    return "Not assigned"
  }

  const text = String(value)
  if (text.length <= 24) {
    return text
  }

  return `${text.slice(0, 10)}...${text.slice(-10)}`
}

const statusLabels = {
  Pending: "Pending",
  Queued: "Queued",
  Processing: "Processing",
  Sent: "Broadcast",
  Funded: "Funded",
  Confirming: "Confirming",
  Confirmed: "Confirmed",
  RetryPending: "Retry pending",
  Failed: "Failed",
}

export const getActionWalletStatusLabel = (status) => {
  if (!status) {
    return "Not allocated"
  }

  return statusLabels[status] || status
}

export const getActionWalletMetrics = (overview) => {
  const allocated = overview?.allocatedAmount ?? overview?.wallet?.allocatedAmount ?? 0
  const committed = overview?.committedTaskPayoutAmount ?? 0
  const available = overview?.availableTaskPayoutAmount ?? Math.max(0, Number(allocated) - Number(committed))

  return {
    allocated: formatBtcAmount(allocated),
    committed: formatBtcAmount(committed),
    available: formatBtcAmount(available),
  }
}

const ActionBtcWalletStatus = ({ actionId }) => {
  const { getAccessTokenSilently } = useAuth0()
  const [overview, setOverview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const fetchOverview = useCallback(
    async (signal) => {
      if (!actionId) {
        setOverview(null)
        setLoading(false)
        return
      }

      setLoading(true)
      setError("")

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
        })
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin
        const response = await fetch(`${apiUrl}/api/Actions/${actionId}/btc-wallet`, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        })

        if (response.status === 404) {
          setOverview(null)
          return
        }

        if (!response.ok) {
          throw new Error(`Unable to load action BTC wallet (${response.status})`)
        }

        setOverview(await response.json())
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(requestError.message || "Unable to load action BTC wallet.")
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false)
        }
      }
    },
    [actionId, getAccessTokenSilently]
  )

  useEffect(() => {
    const abortController = new AbortController()
    fetchOverview(abortController.signal)
    return () => abortController.abort()
  }, [fetchOverview])

  const metrics = getActionWalletMetrics(overview)
  const wallet = overview?.wallet
  const fundingIntent = overview?.fundingIntent
  const taskPayoutCount = overview?.taskPayouts?.length ?? 0

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="h-4 w-4" />
          Action BTC Wallet
        </CardTitle>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => fetchOverview()}
          aria-label="Refresh action BTC wallet"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {error && <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        {loading && !overview ? (
          <div className="text-sm text-gray-500">Loading wallet funding...</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div>
              <div className="text-xs uppercase text-gray-500">Funding</div>
              <div className="font-medium">{getActionWalletStatusLabel(wallet?.fundingStatus || fundingIntent?.status)}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-500">Allocated</div>
              <div className="font-medium">{metrics.allocated}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-500">Committed</div>
              <div className="font-medium">{metrics.committed}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-500">Available</div>
              <div className="font-medium">{metrics.available}</div>
            </div>
          </div>
        )}

        {!loading && overview && (
          <div className="mt-4 grid grid-cols-1 gap-3 border-t pt-4 text-sm md:grid-cols-3">
            <div>
              <div className="text-xs uppercase text-gray-500">Receive Address</div>
              <div className="font-mono">{shortenBtcIdentifier(wallet?.walletAddress)}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-500">Funding Intent</div>
              <div>{fundingIntent?.id ? `#${fundingIntent.id} ${getActionWalletStatusLabel(fundingIntent.status)}` : "Not queued"}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-500">Task Payouts</div>
              <div>{taskPayoutCount}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default ActionBtcWalletStatus
